import { Loader } from '@googlemaps/js-api-loader';
import type { LoaderOptions } from '@googlemaps/js-api-loader';

export interface GoogleMapsConfig {
  apiKey: string;
  version?: string;
  libraries?: ('places' | 'geometry' | 'drawing' | 'visualization')[];
}

export class GoogleMapsService {
  private static instance: GoogleMapsService;
  private initialized = false;
  private loader: Loader | null = null;
  private Place: any = null;


  private constructor() {}

  static getInstance(): GoogleMapsService {
    if (!GoogleMapsService.instance) {
      GoogleMapsService.instance = new GoogleMapsService();
    }
    return GoogleMapsService.instance;
  }

  async initialize(config?: Partial<GoogleMapsConfig>): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const defaultConfig: LoaderOptions = {
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY',
        version: 'weekly',
        libraries: ['places'],
        ...config,
      };

      this.loader = new Loader(defaultConfig);
      await this.loader.load();

      // Import the modern Place class
      const placesLibrary = await google.maps.importLibrary("places") as any;
      this.Place = placesLibrary.Place;

      this.initialized = true;
    } catch (error) {
      console.error('[GoogleMapsService] Error initializing Google Maps:', error);
      throw error;
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // Helper method to create a map instance
  createMap(element: HTMLElement, options: google.maps.MapOptions): google.maps.Map {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }
    return new google.maps.Map(element, options);
  }

  // Helper method to create LatLngBounds
  createLatLngBounds(): google.maps.LatLngBounds {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }
    return new google.maps.LatLngBounds();
  }

  // Helper method to create LatLng
  createLatLng(lat: number, lng: number): google.maps.LatLng {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }
    return new google.maps.LatLng(lat, lng);
  }

  // Helper method to create Size
  createSize(width: number, height: number): google.maps.Size {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }
    return new google.maps.Size(width, height);
  }

  // Modern Places API methods using Place.searchByText
  async searchPlacesByText(
    textQuery: string,
    location: { lat: number; lng: number }
  ): Promise<any[]> {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }

    try {
      const request = {
        textQuery: textQuery,
        fields: [
          'displayName', 
          'location', 
          'businessStatus', 
          'formattedAddress', 
          'types', 
          'rating', 
          'photos',
          'internationalPhoneNumber',
          'nationalPhoneNumber',
          'websiteURI',
          'userRatingCount'
        ],
        locationBias: { lat: location.lat, lng: location.lng },
        language: 'en-US',
        maxResultCount: 50,
        useStrictTypeFiltering: false,
      };

      // Use the modern Place.searchByText method
      const { places } = await this.Place.searchByText(request);
      
      return places || [];
    } catch (error) {
      console.error('[GoogleMapsService] Error in searchPlacesByText:', error);
      return [];
    }
  }

  // Get detailed information about a specific place
  async getPlaceDetails(placeId: string): Promise<any> {
    if (!this.initialized) {
      throw new Error('Google Maps not initialized. Call initialize() first.');
    }

    try {
      const request = {
        placeId: placeId,
        fields: [
          'displayName',
          'location',
          'formattedAddress',
          'types',
          'rating',
          'photos',
          'internationalPhoneNumber',
          'nationalPhoneNumber',
          'websiteURI',
          'userRatingCount'
        ],
        language: 'en-US',
      };

      // Use the modern Place.fetchPlace method
      const place = await this.Place.fetchPlace(request);
      
      return place || null;
    } catch (error) {
      console.error('[GoogleMapsService] Error in getPlaceDetails:', error);
      return null;
    }
  }
}

// Export a singleton instance
export const googleMapsService = GoogleMapsService.getInstance(); 